import {TJobTask} from '../types/activity.types'

export const TASK_WEIGHT_MIN = 0.5
export const TASK_WEIGHT_MAX = 10
export const TASK_WEIGHT_STEP = 0.5
export const TASK_WEIGHT_DEFAULT = 1
export const TASK_LABEL_MAX_LENGTH = 500
export const MAX_TASKS_PER_JOB = 50

/** A fresh editor row: blank label, default weight, no server id. */
export const makeEmptyTaskRow = (): TJobTask => ({
  label: '',
  weight: TASK_WEIGHT_DEFAULT,
})

/**
 * Normalises a typed weight: a blank/whitespace-only or otherwise unparsable
 * input falls back to the default, out-of-range values are clamped, and the
 * result snaps to the half step so the API's one-decimal rule always holds.
 */
export const clampWeight = (raw: string | number): number => {
  if (typeof raw === 'string' && raw.trim() === '') return TASK_WEIGHT_DEFAULT
  const parsed =
    typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'))
  if (!Number.isFinite(parsed)) return TASK_WEIGHT_DEFAULT
  const clamped = Math.min(TASK_WEIGHT_MAX, Math.max(TASK_WEIGHT_MIN, parsed))
  return Math.round(clamped / TASK_WEIGHT_STEP) * TASK_WEIGHT_STEP
}

/**
 * Turns editor rows into the API `tasks` array: blank labels are dropped
 * silently, labels trimmed, and `id` is only present on rows the API already
 * knows.
 */
export const toTaskPayload = (rows: TJobTask[]): TJobTask[] =>
  rows
    .map(row => ({...row, label: row.label.trim()}))
    .filter(row => row.label.length > 0)
    .map(row => ({
      ...(row.id ? {id: row.id} : {}),
      label: row.label,
      weight: row.weight,
    }))

/** Number of tasks whose weight differs from the default. */
export const countWeightedTasks = (tasks: TJobTask[]): number =>
  tasks.filter(task => task.weight !== TASK_WEIGHT_DEFAULT).length

/** A grouped occurrence edited alone has no job scope of its own: its tasks belong to the group. */
export const canEditJobScope = (
  scope: 'single' | 'group' | 'following' | undefined,
  activityGroupId: string | null | undefined,
): boolean => !(scope === 'single' && !!activityGroupId)

/**
 * "Absent" means "leave the job's tasks unchanged" for the API: send only once the tab was opened or the job already had tasks.
 */
export const shouldSendTasks = (
  progressTabOpened: boolean,
  initialTasks: readonly unknown[] | undefined,
): boolean => progressTabOpened || (initialTasks?.length ?? 0) > 0
